'use client'

// CartContext.tsx
import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { ProductType } from '@/type/ProductType';
import { tiktokAddToCart } from '../utils/tiktokPixel';
import { metaAddToCart } from '../utils/metaPixel';

interface CartItem extends ProductType {
    quantity: number
    selectedSize: string
    selectedColor: string
}

interface CartState {
    cartArray: CartItem[]
}

type CartAction =
    | { type: 'ADD_TO_CART'; payload: ProductType }
    | { type: 'REMOVE_FROM_CART'; payload: string }
    | {
        type: 'UPDATE_CART'; payload: {
            itemId: string; quantity: number, selectedSize: string, selectedColor: string
        }
    }
    | { type: 'LOAD_CART'; payload: CartItem[] }

interface CartContextProps {
    cartState: CartState;
    addToCart: (item: ProductType) => void;
    removeFromCart: (itemId: string) => void;
    updateCart: (itemId: string, quantity: number, selectedSize: string, selectedColor: string) => void;
}

const CartContext = createContext<CartContextProps | undefined>(undefined);

/** Call after any direct `localStorage.setItem('cartItems', ...)` so React state (e.g. floating cart badge) stays in sync. */
export const KISSARITI_CART_STORAGE_EVENT = 'kissariti-cart-storage-changed';

export function notifyCartStorageChanged(): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(KISSARITI_CART_STORAGE_EVENT));
}

function mapStoredRowsToCartItems(cartItems: any[]): CartItem[] {
    return cartItems.map((item: any) => ({
        id: item.id,
        quantity: item.quantity || 1,
        selectedSize: item.size || '',
        selectedColor: item.color || '',
        name: '',
        price: 0,
        originPrice: 0,
        brand: '',
        sold: 0,
        quantityPurchase: item.quantity || 1,
        new: false,
        sale: false,
        rate: 0,
        sizes: [],
        variation: [],
        thumbImage: [],
        images: [],
        description: '',
        action: '',
        slug: '',
        category: '',
        type: '',
        gender: ''
    }));
}

const cartReducer = (state: CartState, action: CartAction): CartState => {
    let newState: CartState;
    
    switch (action.type) {
        case 'ADD_TO_CART':
            const newItem: CartItem = { ...action.payload, quantity: 1, selectedSize: '1', selectedColor: '' };
            newState = {
                ...state,
                cartArray: [...state.cartArray, newItem],
            };
            // Sync with localStorage
            const cartItemsJSON = localStorage.getItem("cartItems");
            const cartItems = cartItemsJSON ? JSON.parse(cartItemsJSON) : [];
            const existingItem = cartItems.find((item: any) => Number(item.id) === Number(newItem.id));
            if (!existingItem) {
                cartItems.push({
                    id: newItem.id,
                    quantity: newItem.quantity,
                    size: newItem.selectedSize,
                    color: newItem.selectedColor
                });
                localStorage.setItem("cartItems", JSON.stringify(cartItems));
                notifyCartStorageChanged();
            }
            return newState;
            
        case 'REMOVE_FROM_CART':
            newState = {
                ...state,
                cartArray: state.cartArray.filter((item) => item.id !== action.payload),
            };
            // Sync with localStorage
            const cartItemsToUpdate = localStorage.getItem("cartItems");
            if (cartItemsToUpdate) {
                const items = JSON.parse(cartItemsToUpdate);
                const updatedItems = items.filter((item: any) => Number(item.id) !== Number(action.payload));
                localStorage.setItem("cartItems", JSON.stringify(updatedItems));
                notifyCartStorageChanged();
            }
            return newState;
            
        case 'UPDATE_CART':
            newState = {
                ...state,
                cartArray: state.cartArray.map((item) =>
                    item.id === action.payload.itemId
                        ? {
                            ...item,
                            quantity: action.payload.quantity,
                            selectedSize: action.payload.selectedSize,
                            selectedColor: action.payload.selectedColor
                        }
                        : item
                ),
            };
            // Sync with localStorage
            const cartItemsToUpdate2 = localStorage.getItem("cartItems");
            if (cartItemsToUpdate2) {
                const items = JSON.parse(cartItemsToUpdate2);
                const updatedItems = items.map((item: any) => 
                    Number(item.id) === Number(action.payload.itemId)
                        ? {
                            ...item,
                            quantity: action.payload.quantity,
                            size: action.payload.selectedSize,
                            color: action.payload.selectedColor
                        }
                        : item
                );
                localStorage.setItem("cartItems", JSON.stringify(updatedItems));
                notifyCartStorageChanged();
            }
            return newState;
            
        case 'LOAD_CART':
            return {
                ...state,
                cartArray: action.payload,
            };
        default:
            return state;
    }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cartState, dispatch] = useReducer(cartReducer, { cartArray: [] });

    const reloadCartFromStorage = useCallback(() => {
        const cartItemsJSON = localStorage.getItem("cartItems");
        if (!cartItemsJSON) {
            dispatch({ type: 'LOAD_CART', payload: [] });
            return;
        }
        try {
            const cartItems = JSON.parse(cartItemsJSON);
            const cartArray = mapStoredRowsToCartItems(Array.isArray(cartItems) ? cartItems : []);
            dispatch({ type: 'LOAD_CART', payload: cartArray });
        } catch (error) {
            console.error('Error loading cart from localStorage:', error);
        }
    }, []);

    // Initial load + stay in sync when other code updates localStorage (e.g. product page add-to-cart)
    useEffect(() => {
        reloadCartFromStorage();
    }, [reloadCartFromStorage]);

    useEffect(() => {
        const onExternalCartWrite = () => reloadCartFromStorage();
        window.addEventListener(KISSARITI_CART_STORAGE_EVENT, onExternalCartWrite);
        return () => window.removeEventListener(KISSARITI_CART_STORAGE_EVENT, onExternalCartWrite);
    }, [reloadCartFromStorage]);

    const addToCart = (item: ProductType) => {
        dispatch({ type: 'ADD_TO_CART', payload: item });
        const qty = Number(item.quantityPurchase) || 1;
        const unitPrice = Number(item.originPrice ?? item.price ?? 0);
        tiktokAddToCart({
            contentId: String(item.id),
            contentName: String(item.name || ''),
            quantity: qty,
            value: unitPrice * qty,
        });
        metaAddToCart({
            contentId: String(item.id),
            contentName: String(item.name || ''),
            quantity: qty,
            value: unitPrice * qty,
        });
    };

    const removeFromCart = (itemId: string) => {
        dispatch({ type: 'REMOVE_FROM_CART', payload: itemId });
    };

    const updateCart = (itemId: string, quantity: number, selectedSize: string, selectedColor: string) => {
        dispatch({ type: 'UPDATE_CART', payload: { itemId, quantity, selectedSize, selectedColor } });
    };

    return (
        <CartContext.Provider value={{ cartState, addToCart, removeFromCart, updateCart }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
